import { useState, useEffect, useCallback } from 'react';
import { FaPlus, FaTrash, FaShieldAlt, FaUser, FaUsers, FaBuilding } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useAuth } from '@/context/AuthContext';
import * as Sentry from '@sentry/react';

/**
 * BoardSettingsPermissionsTab Component
 *
 * Manages which users, roles, or departments can modify board settings
 * (beyond the board owner and Admin role users who always have access).
 *
 * This component is embedded inline within the BoardSettingsModal's "Access Control" tab.
 *
 * @param {Object} props Component props
 * @param {number} props.boardId - The board ID to configure settings permissions for
 */
const BoardSettingsPermissionsTab = ({ boardId }) => {
    const { apiFetch } = useAuth();
    const [permissions, setPermissions] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [roles, setRoles] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [granting, setGranting] = useState(false);
    const [granteeType, setGranteeType] = useState('Department');
    const [selectedGrantee, setSelectedGrantee] = useState('');

    /**
     * Fetch current settings permissions for this board
     */
    const fetchPermissions = useCallback(async () => {
        try {
            const res = await apiFetch(`/api/KanbanBoards/${boardId}/settings-permissions`);
            const data = await res.json();
            if (data.success) {
                setPermissions(data.data || []);
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Failed to fetch settings permissions:', error);
        } finally {
            setLoading(false);
        }
    }, [apiFetch, boardId]);

    /**
     * Fetch departments, roles, and users for the dropdowns
     */
    const fetchLookupData = useCallback(async () => {
        try {
            const [deptRes, roleRes, userRes] = await Promise.all([
                apiFetch('/api/Department'),
                apiFetch('/api/User/roles'),
                apiFetch('/api/User/view')
            ]);
            const [deptData, roleData, userData] = await Promise.all([
                deptRes.json(),
                roleRes.json(),
                userRes.json()
            ]);

            if (deptData.success) setDepartments(deptData.data || []);
            if (roleData.success) setRoles(roleData.data || []);
            if (userData.success) {
                // Filter to active users only
                const activeUsers = (userData.data || []).filter(u => u.isActive);
                setUsers(activeUsers);
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Failed to fetch lookup data:', error);
        }
    }, [apiFetch]);

    useEffect(() => {
        const loadData = async () => {
            await Promise.all([fetchPermissions(), fetchLookupData()]);
        };
        loadData();
    }, [fetchPermissions, fetchLookupData]);

    /**
     * Get the display name for the selected grantee based on the type and ID
     */
    const getGranteeName = (type, granteeId) => {
        const id = parseInt(granteeId);
        switch (type) {
            case 'Department': {
                const dept = departments.find(d => d.departmentID === id || d.id === id);
                return dept?.departmentName || dept?.name || `Department ${id}`;
            }
            case 'Role': {
                const role = roles.find(r => r.roleID === id || r.id === id);
                return role?.roleName || role?.name || `Role ${id}`;
            }
            case 'User': {
                const user = users.find(u => u.userID === id || u.id === id);
                return user ? `${user.firstName} ${user.lastName}` : `User ${id}`;
            }
            default:
                return `${type} ${granteeId}`;
        }
    };

    /**
     * Grant settings permission to the selected grantee
     */
    const handleGrant = async () => {
        if (!selectedGrantee) {
            toast.warning('Please select a grantee');
            return;
        }

        setGranting(true);
        try {
            const granteeName = getGranteeName(granteeType, selectedGrantee);
            const res = await apiFetch(`/api/KanbanBoards/${boardId}/settings-permissions`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    granteeType,
                    granteeId: parseInt(selectedGrantee),
                    granteeName
                })
            });
            const data = await res.json();
            if (data.success) {
                toast.success('Settings permission granted');
                setSelectedGrantee('');
                await fetchPermissions();
            } else {
                toast.error(data.message || 'Failed to grant permission');
            }
        } catch (error) {
            Sentry.captureException(error);
            toast.error('Failed to grant permission');
        } finally {
            setGranting(false);
        }
    };

    /**
     * Revoke a settings permission entry
     */
    const handleRevoke = async (permissionId) => {
        if (!window.confirm('Revoke this settings permission?')) return;
        try {
            const res = await apiFetch(`/api/KanbanBoards/${boardId}/settings-permissions/${permissionId}`, {
                method: 'DELETE'
            });
            const data = await res.json();
            if (data.success) {
                toast.success('Permission revoked');
                await fetchPermissions();
            } else {
                toast.error(data.message || 'Failed to revoke');
            }
        } catch (error) {
            Sentry.captureException(error);
            toast.error('Failed to revoke permission');
        }
    };

    /**
     * Get the icon component for a grantee type
     */
    const getTypeIcon = (type) => {
        switch (type) {
            case 'User': return <FaUser />;
            case 'Role': return <FaShieldAlt />;
            case 'Department': return <FaBuilding />;
            default: return <FaUsers />;
        }
    };

    /**
     * Get the appropriate options for the currently selected grantee type
     */
    const getGranteeOptions = () => {
        switch (granteeType) {
            case 'Department':
                return departments.map(d => ({
                    value: d.departmentID || d.id,
                    label: d.departmentName || d.name
                }));
            case 'Role':
                return roles.map(r => ({
                    value: r.roleID || r.id,
                    label: r.roleName || r.name
                }));
            case 'User':
                return users.map(u => ({
                    value: u.userID || u.id,
                    label: `${u.firstName} ${u.lastName}`
                }));
            default:
                return [];
        }
    };

    if (loading) {
        return <div className="board-settings-permissions-loading">Loading settings permissions...</div>;
    }

    return (
        <div className="board-settings-permissions">
            {/* Header explanation */}
            <div className="board-settings-permissions__info">
                <FaShieldAlt style={{ marginRight: '8px', color: '#6c5ce7' }} />
                <span>
                    Configure who can modify this board&apos;s settings (columns, automation rules, permissions, etc.).
                    Board owners and Admin users always have access.
                </span>
            </div>

            {/* Grant form */}
            <div className="board-settings-permissions__grant-form">
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                    {/* Grantee type selector */}
                    <select
                        value={granteeType}
                        onChange={(e) => { setGranteeType(e.target.value); setSelectedGrantee(''); }}
                        className="board-settings-permissions__select"
                        style={{ minWidth: '130px' }}
                    >
                        <option value="Department">Department</option>
                        <option value="Role">Role</option>
                        <option value="User">User</option>
                    </select>

                    {/* Grantee dropdown */}
                    <select
                        value={selectedGrantee}
                        onChange={(e) => setSelectedGrantee(e.target.value)}
                        className="board-settings-permissions__select"
                        style={{ flex: 1, minWidth: '180px' }}
                    >
                        <option value="">-- Select {granteeType} --</option>
                        {getGranteeOptions().map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>

                    {/* Grant button */}
                    <button
                        onClick={handleGrant}
                        disabled={granting || !selectedGrantee}
                        className="board-settings-permissions__grant-btn"
                    >
                        <FaPlus /> Grant Access
                    </button>
                </div>
            </div>

            {/* Existing permissions list */}
            <div className="board-settings-permissions__list">
                {permissions.length === 0 ? (
                    <p className="board-settings-permissions__empty">
                        No additional settings permissions configured. Only the board owner and Admin users can modify settings.
                    </p>
                ) : (
                    <table className="board-settings-permissions__table">
                        <thead>
                            <tr>
                                <th style={{ textAlign: 'left' }}>Type</th>
                                <th style={{ textAlign: 'left' }}>Name</th>
                                <th style={{ textAlign: 'left' }}>Granted</th>
                                <th style={{ textAlign: 'left' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {permissions.map(perm => (
                                <tr key={perm.id}>
                                    <td>
                                        <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                            {getTypeIcon(perm.granteeType)}
                                            {perm.granteeType}
                                        </span>
                                    </td>
                                    <td>{perm.granteeName || `ID: ${perm.granteeId}`}</td>
                                    <td>{new Date(perm.createdDate).toLocaleDateString()}</td>
                                    <td>
                                        <button
                                            onClick={() => handleRevoke(perm.id)}
                                            className="board-settings-permissions__revoke-btn"
                                            title="Revoke permission"
                                        >
                                            <FaTrash />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default BoardSettingsPermissionsTab;
